import { trpc } from './trpc';

/**
 * Mirror of `apps/api/src/shared/permissions.ts` — the canonical
 * permission keys recognised by the backend's `requirePermission(...)`
 * middleware. When a new key is added there, append it here so callers
 * get type-checked autocomplete on `can(...)`.
 */
export type PermissionKey =
  | 'messaging'
  | 'chat_assigned_only'
  | 'chat_export'
  | 'manage_contacts'
  | 'export_contacts'
  | 'view_contact_phone'
  | 'manage_campaigns'
  | 'view_campaign_reports'
  | 'manage_bots'
  | 'manage_templates'
  | 'manage_integrations'
  | 'manage_team'
  | 'view_billing'
  | 'manage_billing';

/**
 * Single source of truth for the vendor app's permission gating. Reads
 * the current session's `auth.me` payload (cached by React Query) and
 * exposes a `can(permission)` helper plus role flags.
 *
 * Admin bypass mirrors the server's `requirePermission` semantics
 * (`apps/api/src/trpc/trpc.ts:48-50`): both VendorAdmin and SuperAdmin
 * pass every check regardless of the explicit permission array.
 */
export function usePermissions() {
  const me = trpc.auth.me.useQuery(undefined, { retry: false });
  const isSuperAdmin = me.data?.roles?.includes('SuperAdmin') ?? false;
  const isVendorAdmin = me.data?.roles?.includes('VendorAdmin') ?? false;
  const permissions = (me.data?.permissions ?? []) as PermissionKey[];
  const can = (permission: PermissionKey): boolean =>
    isSuperAdmin || isVendorAdmin || permissions.includes(permission);
  return {
    isLoading: me.isPending,
    isSuperAdmin,
    isVendorAdmin,
    permissions,
    can,
  };
}
